import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { S as StringService } from './index2.js';
import { D as DomService, K as Key } from './index3.js';
import { T as TranslationService } from './index6.js';
import { d as defineCustomElement$3 } from './roche-icon2.js';
import { d as defineCustomElement$2 } from './roche-tooltip2.js';

const rocheDropdownCss = ":host{display:block;margin-top:3rem}@media (min-width: 90rem){:host{margin-top:4.5rem}}:host,:host *{box-sizing:border-box}:host select{margin:0;border-radius:0;background-color:transparent;appearance:none}:host select:active,:host select:focus{outline:none}:host select::-ms-expand{display:none}:host .label{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-weight:400;font-size:var(--label--font-size--small-up);line-height:1.3125rem;display:block;margin-bottom:0.5rem;color:#544f4f;pointer-events:none}@media (min-width: 48rem){:host .label{font-size:var(--label--font-size--medium-up);line-height:1.3125rem}}:host .label-wrapper{display:flex;position:relative}:host .dropdown{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:var(--body-small--font-size--small-up);line-height:1.625rem;width:100%;border:1px solid #ccc;border-bottom:1px solid #333;padding:0.75rem 3rem 0.75rem 1.0625rem;cursor:pointer}@media (min-width: 48rem){:host .dropdown{font-size:var(--body-small--font-size--medium-up);line-height:1.8125rem}}@supports (-webkit-backdrop-filter: blur(1px)){:host .dropdown{-webkit-appearance:none}}:host .dropdown--placeholder{color:#706b69}:host .dropdown:hover{border-bottom-color:var(--color-interactive--primary-idle)}:host .dropdown:active,:host .dropdown:focus{border-color:var(--color-interactive--primary-idle)}:host .dropdown-container{position:relative}:host .dropdown-container roche-icon{position:absolute;top:calc(50% - 0.5rem);right:1rem;fill:#666;pointer-events:none}:host .error{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-weight:400;font-size:var(--label--font-size--small-up);line-height:1.3125rem;margin-top:0.25rem;color:var(--color-status--error)}@media (min-width: 48rem){:host .error{font-size:var(--label--font-size--medium-up);line-height:1.3125rem}}:host .element-container--disabled .dropdown-container{cursor:not-allowed}:host .element-container--disabled .dropdown-container::before{content:\"\";position:absolute;top:0;left:0;width:100%;height:100%;opacity:0.05;background-color:#000}:host .element-container--disabled .label{color:var(--color-interactive--disabled)}:host .element-container--disabled .dropdown{border-color:transparent;color:var(--color-interactive--disabled)}:host .element-container--disabled roche-icon{fill:var(--color-interactive--disabled)}:host .element-container--error .dropdown{border-color:var(--color-status--error)}:host .element-container--error .dropdown:hover{border-bottom-color:var(--color-status--error)}:host .element-container--error .dropdown:active,:host .element-container--error .dropdown:focus{border-color:var(--color-status--error)}:host roche-tooltip{margin:0 0 0 0.625rem}:host([dir=rtl]) .dropdown{padding:0.75rem 1.0625rem 0.75rem 3rem}:host([dir=rtl]) .dropdown-container roche-icon{left:1rem;right:auto}";
const RocheDropdownStyle0 = rocheDropdownCss;

const RocheDropdown$1 = /*@__PURE__*/ proxyCustomElement(class RocheDropdown extends HTMLElement {
    get dropdownStylesParsed() {
        return typeof this.dropdownStyles === 'string' ? JSON.parse(this.dropdownStyles) : this.dropdownStyles;
    }
    get optionsParsed() {
        const parsed = StringService.stringToObject(this.options);
        return Object.keys(parsed).map((key) => parsed[key]);
    }
    get isSelectedValid() {
        if (this.selected === null) {
            return false;
        }
        return this.selected >= 0 && this.selected < this.optionsParsed.length;
    }
    get valid() {
        return this.required ? this.isSelectedValid : true;
    }
    get value() {
        return this.isSelectedValid ? this.optionsParsed[this.selected].value : undefined;
    }
    handleRocheFormControlInit() {
        this.formControlChangeEvent.emit(this);
    }
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.formControlChangeEvent = createEvent(this, "rocheFormControlChange", 7);
        this.changeEvent = createEvent(this, "rocheDropdownChange", 7);
        this.uid = undefined;
        this.name = undefined;
        this.label = undefined;
        this.tooltip = undefined;
        this.required = false;
        this.options = undefined;
        this.selected = undefined;
        this.placeholder = undefined;
        this.error = undefined;
        this.disabled = false;
        this.dropdownStyles = undefined;
        this.dirty = false;
        this.open = false;
        this.translations = undefined;
        this.uniqueId = `roche-dropdown-${StringService.generate()}`;
        this.handleChanged = this.handleChanged.bind(this);
        this.handleToggle = this.handleToggle.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
    }
    async componentWillLoad() {
        this.translations = {
            optional: await TranslationService.translate('optional'),
        };
        if (!this.isSelectedValid && this.placeholder === undefined) {
            this.selected = 0;
        }
    }
    componentDidLoad() {
        DomService.handleTextDirection(this.element);
    }
    render() {
        const hasError = this.dirty && !this.valid;
        const options = this.optionsParsed;
        const containerClasses = {
            'element-container': true,
            'element-container--error': hasError,
            'element-container--open': this.open,
            'element-container--disabled': this.disabled,
        };
        const isRenderLabel = !!(this.label || this.required);
        return (h("div", { key: '8711cfb943157353f8a60e7d86b78d9c511c590d', class: containerClasses }, isRenderLabel
            && h("div", { class: "label-wrapper" }, h("label", { class: "label", htmlFor: this.uniqueId }, this.label, !this.required ? ` (${StringService.capitalize(this.translations.optional)})` : ''), this.tooltip
                && h("roche-tooltip", null, this.tooltip)), h("div", { key: 'ca40d5bb3059f155365c196fe094c2b83d380c30', class: "dropdown-container" }, h("select", { key: '6958ad8f0eedb60f8677439ab5c16603f32f55a2', style: this.dropdownStylesParsed, id: this.uniqueId, class: { dropdown: true, 'dropdown--placeholder': !this.open && !this.isSelectedValid }, disabled: this.disabled, onChange: this.handleChanged, onClick: this.handleToggle, onKeyDown: this.handleToggle, onBlur: this.handleBlur }, !this.isSelectedValid && h("option", { disabled: true, selected: true }, this.placeholder), options.map(({ label }, index) => (h("option", { key: `${this.uniqueId}-${index}`, value: index, selected: this.isSelectedValid && this.selected === index, onClick: this.handleClose, onKeyDown: this.handleClose }, label)))), h("roche-icon", { key: '17f7be971925c3e57092ddcf89028acd186407e0', icon: "chevron-down", width: "16px" })), h("div", { key: '49d338db04973baf54597400c5d51e742a2253f9', class: "error" }, hasError && !this.disabled && this.error ? this.error : '')));
    }
    handleChanged({ target }) {
        const index = parseInt(target.value, 10);
        if (this.selected !== index) {
            const newIndex = index >= 0 && index < this.optionsParsed.length ? index : 0;
            const item = this.optionsParsed[newIndex];
            this.selected = index;
            this.changeEvent.emit({
                index: newIndex,
                value: item,
            });
            this.formControlChangeEvent.emit(this);
        }
    }
    handleToggle(event) {
        var _a;
        const { keyCode } = event;
        if ((!keyCode || keyCode === Key.SPACE) && ((_a = event === null || event === void 0 ? void 0 : event.target) === null || _a === void 0 ? void 0 : _a.tagName) !== 'OPTION') {
            this.open = !this.open;
        }
    }
    handleClose(event) {
        const { keyCode } = event || {};
        if (!keyCode || keyCode === Key.ESCAPE || keyCode === Key.ENTER) {
            this.open = false;
        }
    }
    handleBlur() {
        this.handleClose();
        this.dirty = true;
    }
    get element() { return this; }
    static get style() { return RocheDropdownStyle0; }
}, [1, "roche-dropdown", {
        "uid": [1],
        "name": [1],
        "label": [1],
        "tooltip": [1],
        "required": [4],
        "options": [1],
        "selected": [1026],
        "placeholder": [1],
        "error": [1],
        "disabled": [4],
        "dropdownStyles": [1, "dropdown-styles"],
        "dirty": [32],
        "open": [32],
        "translations": [32]
    }, [[0, "rocheFormControlInit", "handleRocheFormControlInit"]]]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-dropdown", "roche-icon", "roche-tooltip"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-dropdown":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheDropdown$1);
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "roche-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const RocheDropdown = RocheDropdown$1;
const defineCustomElement = defineCustomElement$1;

export { RocheDropdown, defineCustomElement };

//# sourceMappingURL=roche-dropdown.js.map